import { useTranslation } from 'react-i18next';
import { useToasts } from 'react-toast-notifications';
import { IdentificationTagsDto } from 'api/identificationTags/models/IdentificationTagsDto';
import Loading from 'common/services/Loading';
import IdentificationTagsService from 'api/identificationTags/IdentificationTagsService';
import Logger from 'common/services/Logger';
import Button from 'common/components/button/Button';
import styles from './IdentificationTagDetails.module.scss';
import InputError from 'common/components/inputError/InputError';
import Input from 'common/components/input/Input';
import Label from 'common/components/label/Label';
import FormItem from 'common/components/formItem/FormItem';
import FormController from 'common/components/formController/FormController';
import { useForm } from 'react-hook-form';
import { DEFAULT_INPUT_RULES_WITH_REQUIRED, LOGGER_LOG_TYPE, removeAccents } from 'Config';
import GreyArrow from 'assets/svg/Arrow-grey.svg';
import SelectController from 'common/components/select/SelectController';
import { SelectValueLabel } from 'common/types/SelectValueLabel';
import { useEffect, useState } from 'react';
import { Col, Row } from 'react-flexbox-grid';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import React from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { UserProfile } from 'api/account/models/UserProfile';
import { useSelector } from 'react-redux';
import { Reducers } from 'store/types';
import { CheckAvailabilityDto } from 'api/common/models/CheckAvailabilityDto';
import UsersService from 'api/users/UsersService';

type Props = {
};

const IdentificationTagDetails: React.FC<Props> = ({ }: Props) => {
    const { t } = useTranslation();
    const { addToast } = useToasts();

    const history = useHistory();
    // eslint-disable-next-line
    let { type, itemId } = useParams<{ type: string, itemId: string }>();
    
    const [usersOptions, setUsersOptions] = useState<SelectValueLabel[]>([]);

    const [itemToRemove, setItemToRemove] = useState<IdentificationTagsDto>();
    const [dialogDeleteItemIsOpen, setDialogDeleteItemIsOpen] = React.useState(false);

    const [identificationTag, setIdentificationTag] = useState<IdentificationTagsDto | null>(null);

    const form = useForm<IdentificationTagsDto>({ shouldUnregister: false, defaultValues: { ...identificationTag } });

    const { register, handleSubmit, errors, reset } = form;

    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasIdentificationTagsWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['SETTINGUP_IDENTIFICATION_TAGS_WRITE']);
    
    const getData = async () => {
        Loading.show();

        try {
            if (itemId) {
                const model = await IdentificationTagsService.getById(itemId);
                setIdentificationTag(model);
                reset(model);
            }
            const users = await UsersService.catalog();
            setUsersOptions(users);

            Loading.hide();

        } catch (error) {
            addToast(t('common.messages.error_load_info'), {
                appearance: 'error',
            });
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't get identification tags`, error);
            Loading.hide();
        }
    }

    const onSubmit = async (formData: IdentificationTagsDto) => {
        try {
            Loading.show();


            const itData: CheckAvailabilityDto = { table: 'identification_tags', column: 'code', value: formData.code, companyId: true, itemEdit: identificationTag?.id };
            const checkAvailabilityCode = await IdentificationTagsService.checkAvailability(itData);
            if (!checkAvailabilityCode) {
                addToast(t('common.messages.code_already_exists'), { appearance: 'warning' });
                Loading.hide();
                return;
            }
            
            if (identificationTag != null) {
                await IdentificationTagsService.update({ ...identificationTag, ...formData });
            } else {
                itemId = await IdentificationTagsService.create(formData);
                getData();
            }

            Loading.hide();

            addToast(t('common.messages.record_save_success'), { appearance: 'success' });
            history.goBack();
        } catch (error) {
            Loading.hide();
            
            if(error?.response?.status === 409){
                addToast(t('common.messages.name_already_exists'), { appearance: 'warning' });
                return;
            }

            addToast(t('common.messages.record_save_error'), { appearance: 'error' });
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't create or update identification tags`, error);
        }
    };

    const showRemoveItemDialog = (item: IdentificationTagsDto) => {
        setItemToRemove(item);
        setDialogDeleteItemIsOpen(true);
    };

    const removeItem = async () => {
        setDialogDeleteItemIsOpen(false);
        if (!!itemToRemove) {
            try {
                Loading.show();
                await IdentificationTagsService.remove(itemToRemove);
                addToast(t('common.messages.record_delete_success'), { appearance: 'success' });
                history.push(`/settings/identificationTags/list`);
                Loading.hide();
            } catch (error) {
                addToast(t('common.messages.record_delete_error'), { appearance: 'error' });
                Loading.hide();
            }
        }
    };

    useEffect(() => {
        getData();
    }, []);

    const onGoBack = () => {
        if(identificationTag){
            reset(identificationTag);
        }
        history.goBack();
    }

    return (
        <div>
            <div className={styles.pageNameContainer}>
                <div className={styles.pageNameContent} onClick={() => history.push(`/settings/identificationTags/list`)}>
                    <img className={styles.pageNameContentImage} src={GreyArrow} />
                    <span className={styles.pageNameContentText}>
                        {t('settings.identification_tags')}
                    </span>
                </div>
            </div>

            <form className={styles.form} onSubmit={handleSubmit(onSubmit)}>
                <FormController>
                    <Row>
                        <Col xs={12} md={12} lg={12} className={styles.col}>
                            <FormItem>
                                <Label className={styles.label}>{t('identification_tags.code')} {type !== 'details' ? '*' : ''}</Label>
                                <Input
                                    name='code'
                                    placeholder={t('identification_tags.code')}
                                    ref={register({ ...DEFAULT_INPUT_RULES_WITH_REQUIRED })}
                                    disabled={type === 'details'}
                                />
                                <InputError error={errors.code} maxLength={250}/>
                            </FormItem>
                        </Col>
                        <Col xs={12} md={6} lg={6} className={styles.col}>
                            <FormItem>
                                <Label className={styles.label}>{t('identification_tags.user')} {type !== 'details' ? '*' : ''}</Label>
                                <SelectController
                                    form={form}
                                    name='userId'
                                    menuPortalTarget={document.querySelector('body')}
                                    options={usersOptions}
                                    placeholder={t('identification_tags.user')}
                                    rules={{ required: true }}
                                    isDisabled={type === 'details'}
                                    filterOption={(candidate: any, input: any) => input ? removeAccents(candidate.label).toUpperCase().includes(removeAccents(input).toUpperCase()) : true}
                                />
                                <InputError error={errors.userId} />
                            </FormItem>
                        </Col>

                    </Row>
                </FormController>

                <div className={styles.buttonContainer}>
                    <Button
                        preset={'secondary'}
                        type='button'
                        text={t('common.cancel')}
                        onClick={() => onGoBack()}
                    />
                    {type === 'details' && hasIdentificationTagsWritePolicy &&
                        <Button
                            type="button"
                            text={t('common.remove')}
                            preset={'danger'}
                            onClick={() => showRemoveItemDialog({ id: identificationTag?.id } as IdentificationTagsDto)} />
                    }
                    {type === 'details' && hasIdentificationTagsWritePolicy && <Button type='button' text={t('common.edit')} onClick={() => history.push(`/settings/identificationTags/edit/${itemId}`)} />}
                    {type !== 'details' && hasIdentificationTagsWritePolicy && <Button type='submit' text={t('common.save')} />}

                    <QuestionYesNo isVisible={dialogDeleteItemIsOpen} onNo={() => setDialogDeleteItemIsOpen(false)} onYes={() => removeItem()} message={t('common.messages.remove_record_with_ident', { name: itemToRemove?.code ?? '' })}/>

                </div>
            </form>
        </div>
    );
};

export default IdentificationTagDetails;
